import { trackEvent } from '@pm/analytics';
import {
  AppointmentFragment,
  EncounterType,
  PatientAppointmentPartsFragment,
} from '@pm/graphql';
import { Button, Icon, Modal } from '@pm/ui';
import { google, ics, outlook } from 'calendar-link';
import { isValid, parseISO } from 'date-fns';
import { ComponentProps, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useEnvironment } from '../../hooks/env';

type EncounterTypeKey = 'Video' | 'Phone' | 'InPerson';

const EncounterTypeKeyForType: Record<
  Exclude<EncounterType, EncounterType.Message>,
  EncounterTypeKey
> = {
  [EncounterType.Video]: 'Video',
  [EncounterType.Phone]: 'Phone',
  [EncounterType.InPerson]: 'InPerson',
};

type AppointmentWithCalendarFields = {
  scheduledAt: string;
  durationInMinutes: number;
  location: string | null;
};

type AddToCalendarButtonProps = {
  appointment:
    | AppointmentFragment
    | PatientAppointmentPartsFragment
    | AppointmentWithCalendarFields;
  encounterType: Exclude<EncounterType, EncounterType.Message>;
  intent?: ComponentProps<typeof Button>['intent'];
};

export const AddToCalendarButton = ({
  appointment,
  encounterType,
  intent = 'primary',
}: AddToCalendarButtonProps) => {
  const { t } = useTranslation('appointments', {
    keyPrefix: 'CalendarIntegration',
  });
  const [open, setOpen] = useState(false);
  const { brandDisplayName: brand } = useEnvironment();

  const startDate = parseISO(appointment.scheduledAt ?? '');

  // toISOString() throws on an Invalid Date, so an unparseable scheduledAt has
  // to hide the button the same way a missing one does.
  if (!appointment.scheduledAt || !isValid(startDate)) {
    return null;
  }

  const endDate = new Date(
    startDate.getTime() + appointment.durationInMinutes * 60 * 1000,
  );
  const encounterTypeKey = EncounterTypeKeyForType[encounterType];

  const event = {
    title: `${t(`${encounterTypeKey}.Title`, { brand })}`,
    start: startDate.toISOString(),
    end: endDate.toISOString(),
    location: appointment.location || undefined,
  };

  const googleUrl = google(event);
  const outlookUrl = outlook(event);
  const icsFile = ics(event);

  const handleGoogleCalendar = () => {
    trackEvent('Button Clicked', {
      buttonText: 'Google Calendar',
      buttonName: 'add_to_calendar_google',
      currentPath: window.location.pathname,
      componentContext: 'add_to_calendar_modal',
    });
    window.open(googleUrl, '_blank');
    setOpen(false);
  };

  const handleOutlook = () => {
    trackEvent('Button Clicked', {
      buttonText: 'Outlook',
      buttonName: 'add_to_calendar_outlook',
      currentPath: window.location.pathname,
      componentContext: 'add_to_calendar_modal',
    });
    window.open(outlookUrl, '_blank');
    setOpen(false);
  };

  const handleApple = () => {
    trackEvent('Button Clicked', {
      buttonText: 'Apple',
      buttonName: 'add_to_calendar_apple',
      currentPath: window.location.pathname,
      componentContext: 'add_to_calendar_modal',
    });
    window.open(icsFile);
    setOpen(false);
  };

  return (
    <Modal.Root open={open} onOpenChange={setOpen}>
      <Modal.Trigger>
        <Button
          width="full"
          intent={intent}
          leadingIcon={<Icon name="calendar_today" />}
        >
          {t('Actions.AddToCalendar')}
        </Button>
      </Modal.Trigger>
      <Modal.Content title="Add to Calendar">
        <Modal.Body>
          <div className="flex flex-col gap-xs">
            <button
              type="button"
              className="text-left p-s rounded-xs hover:bg-primary hover:text-content-inverse focus:bg-primary focus:text-content-inverse transition-colors"
              onClick={handleGoogleCalendar}
            >
              Google Calendar
            </button>
            <button
              type="button"
              className="text-left p-s rounded-xs hover:bg-primary hover:text-content-inverse focus:bg-primary focus:text-content-inverse transition-colors"
              onClick={handleOutlook}
            >
              Outlook
            </button>
            <button
              type="button"
              className="text-left p-s rounded-xs hover:bg-primary hover:text-content-inverse focus:bg-primary focus:text-content-inverse transition-colors"
              onClick={handleApple}
            >
              Apple
            </button>
          </div>
        </Modal.Body>
      </Modal.Content>
    </Modal.Root>
  );
};
